/**
 * 🔄 VERSION DETECTOR - Detecta Novas Versões Automaticamente
 * Monitora mudanças na aplicação e oferece update com cache busting
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Modal, Button, notification, Progress, Spin } from 'antd';
import { ReloadOutlined, RocketOutlined, CloseOutlined, InfoCircleOutlined } from '@ant-design/icons';

// 🔧 CONTROLE GLOBAL PARA EVITAR MÚLTIPLAS VERIFICAÇÕES SIMULTÂNEAS
const GLOBAL_VERSION_CHECK = {
  isChecking: false,
  lastCheck: 0,
  cooldown: 5 * 60 * 1000, // 5 minutos de cooldown
  
  canCheck(): boolean {
    const now = Date.now();
    const timeSinceLastCheck = now - this.lastCheck;
    
    if (this.isChecking) {
      return false;
    }
    
    if (timeSinceLastCheck < this.cooldown) {
      return false;
    }
    
    return true;
  },
  
  startCheck(): void {
    this.isChecking = true;
    this.lastCheck = Date.now();
  },
  
  endCheck(): void {
    this.isChecking = false;
  }
};

// Importar versão do package.json
const packageJson = require('../../../package.json');

// 🔧 CONFIGURAÇÕES DE COMPORTAMENTO
const HOTFIX_AUTO_RELOAD = process.env.REACT_APP_HOTFIX_AUTO_RELOAD !== 'false'; // Default: true
const HOTFIX_RELOAD_DELAY = parseInt(process.env.REACT_APP_HOTFIX_RELOAD_DELAY || '3000'); // Default: 3s
const VERSION_CHECK_ONLY_SEMVER = process.env.REACT_APP_VERSION_CHECK_ONLY_SEMVER !== 'false'; // Default: true

interface VersionInfo {
  version: string;
  buildHash: string;
  timestamp: number;
  releaseNotes?: string[];
  isVersionChange?: boolean; // Para distinguir entre mudança de versão vs hotfix
}

interface VersionDetectorState {
  currentVersion: VersionInfo | null;
  latestVersion: VersionInfo | null;
  updateAvailable: boolean;
  showUpdateModal: boolean;
  isUpdating: boolean;
  updateProgress: number;
  lastChecked: number;
  isCurrentVersionView: boolean;
}

const VersionDetector: React.FC = () => {
  const [state, setState] = useState<VersionDetectorState>({
    currentVersion: null,
    latestVersion: null,
    updateAvailable: false,
    showUpdateModal: false,
    isUpdating: false,
    updateProgress: 0,
    lastChecked: 0,
    isCurrentVersionView: false
  });

  // 🛑 CIRCUIT BREAKER para evitar loops infinitos
  const [errorCount, setErrorCount] = useState(0);
  const [circuitOpen, setCircuitOpen] = useState(false);
  const MAX_ERRORS = 3;
  const CIRCUIT_TIMEOUT = 5 * 60 * 1000; // 5 minutos

  // 🔧 REFS para acessar valores atuais sem dependências
  const stateRef = useRef(state);
  const errorCountRef = useRef(errorCount);
  const circuitOpenRef = useRef(circuitOpen);

  // 🔧 ATUALIZAR REFS quando state muda
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  useEffect(() => {
    errorCountRef.current = errorCount;
  }, [errorCount]);

  useEffect(() => {
    circuitOpenRef.current = circuitOpen;
  }, [circuitOpen]);

  /**
   * 🏷️ OBTER VERSÃO ATUAL DO PACKAGE.JSON
   */
  const getCurrentVersion = useCallback(async (): Promise<VersionInfo> => {
    try {
      const version = packageJson.version;
      const buildTime = process.env.REACT_APP_BUILD_TIME || Date.now().toString();
      
      let buildHash = buildTime;
      try {
        const scripts = Array.from(document.querySelectorAll('script[src*="static/js"]'));
        if (scripts.length > 0) {
          const mainScript = scripts[0] as HTMLScriptElement;
          const hashMatch = mainScript.src.match(/\.([a-f0-9]{8})\./);
          if (hashMatch) {
            buildHash = hashMatch[1];
          }
        }
      } catch (e) {
        // Silent error
      }

      return {
        version,
        buildHash,
        timestamp: Date.now()
      };
    } catch (error) {
      return {
        version: '1.0.0',
        buildHash: 'unknown',
        timestamp: Date.now()
      };
    }
  }, []);

  /**
   * 🌐 VERIFICAR NOVA VERSÃO - ENDPOINT API OU PACKAGE.JSON REMOTO
   */
  const checkForUpdates = useCallback(async (): Promise<VersionInfo | null> => {
    // 🚨 CONTROLE GLOBAL: Verificar se pode fazer a chamada
    if (!GLOBAL_VERSION_CHECK.canCheck()) {
      return null;
    }

    // 🚨 CIRCUIT BREAKER: Para após muitos erros consecutivos
    if (circuitOpenRef.current) {
      return null;
    }

    if (errorCountRef.current >= MAX_ERRORS) {
      setCircuitOpen(true);
      setTimeout(() => {
        setCircuitOpen(false);
        setErrorCount(0);
      }, CIRCUIT_TIMEOUT);
      return null;
    }

    // 🔧 INICIAR VERIFICAÇÃO GLOBAL
    GLOBAL_VERSION_CHECK.startCheck();

    // 🔧 DESENVOLVIMENTO: Verificações limitadas em localhost
    const isDevelopment = process.env.NODE_ENV === 'development' || 
                         window.location.hostname === 'localhost' ||
                         window.location.hostname === '127.0.0.1' ||
                         window.location.port !== '';

    try {
      const safeFetch = window.fetch.bind(window);
      const startTime = performance.now();
      
      // 🎯 PRIORIDADE 1: Tentar endpoint API de versão
      try {
        const versionResponse = await safeFetch('/api/version', {
          cache: 'no-cache',
          headers: { 
            'Cache-Control': 'no-cache',
            'Accept': 'application/json'
          },
          signal: AbortSignal.timeout ? AbortSignal.timeout(5000) : undefined
        });

        if (versionResponse.ok) {
          const endTime = performance.now();
          const duration = endTime - startTime;
          
          
          const data = await versionResponse.json();
          setErrorCount(0);
          
          // 🔧 FINALIZAR VERIFICAÇÃO GLOBAL
          GLOBAL_VERSION_CHECK.endCheck();
          return data;
        }
      } catch (error) {
        // 🔇 DESENVOLVIMENTO: Não loggar erro de API inexistente - endpoint /api/version pode não existir
        // Este erro é esperado em desenvolvimento quando a API não está configurada
      }

      // 🎯 PRIORIDADE 2: Verificar package.json remoto (PRINCIPAL)
      try {
        const packageResponse = await safeFetch('/package.json', {
          cache: 'no-cache',
          headers: { 'Cache-Control': 'no-cache' },
          signal: AbortSignal.timeout ? AbortSignal.timeout(5000) : undefined
        });

        if (packageResponse.ok) {
          const contentType = packageResponse.headers.get('content-type');
          
          // 🔧 DESENVOLVIMENTO: Verificar se retornou HTML ao invés de JSON
          if (isDevelopment && contentType && contentType.includes('text/html')) {
            // Em desenvolvimento, package.json não está disponível como arquivo estático
            GLOBAL_VERSION_CHECK.endCheck();
            return null;
          }
          
          const remotePackage = await packageResponse.json();
          
          if (remotePackage.version) {
            setErrorCount(0);
            
            // 🏭 COMPARAR VERSÕES SEMVER
            const currentVersion = packageJson.version;
            const remoteVersion = remotePackage.version;
         
            
            if (remoteVersion !== currentVersion) {
              // ✅ VERSÃO SEMVER DIFERENTE - Mostrar modal
             
              
              const result = {
                version: remoteVersion,
                buildHash: `remote-${Date.now()}`,
                timestamp: Date.now(),
                isVersionChange: true
              };
              
              // 🔧 FINALIZAR VERIFICAÇÃO GLOBAL
              GLOBAL_VERSION_CHECK.endCheck();
              return result;
            } else {
              // 🔧 MESMA VERSÃO - Verificar build hash para hotfix
              try {
                const scripts = Array.from(document.querySelectorAll('script[src*="static/js"]'));
                let currentBuildHash = 'unknown';
                
                if (scripts.length > 0) {
                  const mainScript = scripts[0] as HTMLScriptElement;
                  const hashMatch = mainScript.src.match(/\.([a-f0-9]{8})\./);
                  if (hashMatch) {
                    currentBuildHash = hashMatch[1];
                  }
                }
                
                // Simular build hash remoto baseado em timestamp
                const remoteBuildHash = `build-${Math.floor(Date.now() / 1000)}`;
                
                // Para hotfixes: recarregar automaticamente sem modal
                if (currentBuildHash !== 'unknown' && HOTFIX_AUTO_RELOAD) {
                 
                  
                  // ⚡ RECARREGAR AUTOMATICAMENTE PARA HOTFIXES
                  setTimeout(() => {
                    if (!isDevelopment) {
                    }
                    window.location.reload();
                  }, HOTFIX_RELOAD_DELAY);
                  
                  // 🔧 FINALIZAR VERIFICAÇÃO GLOBAL
                  GLOBAL_VERSION_CHECK.endCheck();
                  return null; // Não mostrar modal
                } else if (currentBuildHash !== 'unknown' && !HOTFIX_AUTO_RELOAD) {
                  
                  // 🔧 FINALIZAR VERIFICAÇÃO GLOBAL
                  GLOBAL_VERSION_CHECK.endCheck();
                  return null;
                }
              } catch (error) {
               
              }
            }
          }
        }
      } catch (error) {
        // 🔇 DESENVOLVIMENTO: Não loggar erro de package.json que retorna HTML
       
      }

      // 🔧 FINALIZAR VERIFICAÇÃO GLOBAL
      GLOBAL_VERSION_CHECK.endCheck();
      return null;

    } catch (error) {
      // 🔧 FINALIZAR VERIFICAÇÃO GLOBAL EM CASO DE ERRO
      GLOBAL_VERSION_CHECK.endCheck();
      
      setErrorCount(prev => prev + 1);
      
      // 🔇 DESENVOLVIMENTO: Não loggar erros de rede em ambiente local
      if (!isDevelopment) {
        if ((window as any).performanceMonitor && (window as any).performanceMonitor.logError) {
          (window as any).performanceMonitor.logError({
            component: 'VersionDetector',
            operation: 'checkForUpdates',
            error: error.message,
            timestamp: Date.now()
          });
        }
      }
      
      return null;
    }
  }, []);

  /**
   * 📋 OBTER ÚLTIMA VERSÃO DO CHANGELOG.JSON
   */
  const getLatestVersionFromChangelog = useCallback(async (): Promise<string | null> => {
    try {
      let changelog;
      
      try {
        changelog = await import('../../app/changelog.json');
        changelog = changelog.default || changelog;
      } catch (importError) {
        const safeFetch = window.fetch.bind(window);
        const response = await safeFetch('/changelog.json', {
          cache: 'no-cache',
          headers: { 'Cache-Control': 'no-cache' }
        });

        if (response.ok) {
          changelog = await response.json();
        } else {
          throw new Error('Changelog não encontrado');
        }
      }

      const versions = Object.keys(changelog).filter(key => {
        return /^\d+\.\d+\.\d+$/.test(key);
      });

      if (versions.length === 0) {
        return null;
      }

      const sortedVersions = versions.sort((a, b) => {
        const parseVersion = (version: string) => {
          const parts = version.split('.').map(n => parseInt(n, 10) || 0);
          return { major: parts[0] || 0, minor: parts[1] || 0, patch: parts[2] || 0 };
        };

        const versionA = parseVersion(a);
        const versionB = parseVersion(b);

        if (versionB.major !== versionA.major) return versionB.major - versionA.major;
        if (versionB.minor !== versionA.minor) return versionB.minor - versionA.minor;
        return versionB.patch - versionA.patch;
      });

      const latestVersion = sortedVersions[0];
      const currentVersion = packageJson.version;

      if (latestVersion !== currentVersion) {
        const isNewer = compareVersions(
          { version: currentVersion, buildHash: '', timestamp: 0 },
          { version: latestVersion, buildHash: '', timestamp: 0 }
        );

        if (isNewer) {
          return latestVersion;
        }
      }

      return null;
    } catch (error) {
      return null;
    }
  }, []);

  /**
   * 🔍 COMPARAR VERSÕES - USANDO SEMVER
   */
  const compareVersions = useCallback((current: VersionInfo, latest: VersionInfo): boolean => {
    if (!latest || !current) return false;
    
    const parseVersion = (version: string) => {
      const parts = version.split('.').map(n => parseInt(n, 10) || 0);
      return { major: parts[0] || 0, minor: parts[1] || 0, patch: parts[2] || 0 };
    };

    const currentParsed = parseVersion(current.version);
    const latestParsed = parseVersion(latest.version);

    if (latestParsed.major > currentParsed.major) return true;
    if (latestParsed.major < currentParsed.major) return false;
    
    if (latestParsed.minor > currentParsed.minor) return true;
    if (latestParsed.minor < currentParsed.minor) return false;
    
    if (latestParsed.patch > currentParsed.patch) return true;

    if (current.version === latest.version && current.buildHash !== latest.buildHash && latest.buildHash !== 'unknown') {
      return true;
    }

    return false;
  }, []);

  /**
   * 📋 CARREGAR CHANGELOG ESPECÍFICO DA NOVA VERSÃO
   */
  const getChangelogForVersion = useCallback(async (version: string): Promise<string[]> => {
    try {
      let changelog;
      
      try {
        changelog = await import('../../app/changelog.json');
        changelog = changelog.default || changelog;
      } catch (importError) {
        const safeFetch = window.fetch.bind(window);
        const response = await safeFetch('/changelog.json', {
          cache: 'no-cache',
          headers: { 'Cache-Control': 'no-cache' }
        });

        if (response.ok) {
          changelog = await response.json();
        } else {
          throw new Error('Changelog não encontrado');
        }
      }

      const versionData = changelog[version];
      
      
      if (versionData) {
        const releaseNotes: string[] = [];
        
        if (versionData.VersionAdd && versionData.VersionAdd.length > 0) {
          releaseNotes.push('🚀 **Novidades:**');
          versionData.VersionAdd.forEach((item: string) => {
            releaseNotes.push(`• ${item}`);
          });
        }

        if (versionData.VersionChange && versionData.VersionChange.length > 0) {
          releaseNotes.push('⚡ **Melhorias:**');
          versionData.VersionChange.forEach((item: string) => {
            releaseNotes.push(`• ${item}`);
          });
        }

        if (versionData.VersionCorrection && versionData.VersionCorrection.length > 0) {
          releaseNotes.push('🔧 **Correções:**');
          versionData.VersionCorrection.forEach((item: string) => {
            releaseNotes.push(`• ${item}`);
          });
        }

        if (versionData.VersionRemoved && versionData.VersionRemoved.length > 0) {
          releaseNotes.push('🗑️ **Removido:**');
          versionData.VersionRemoved.forEach((item: string) => {
            releaseNotes.push(`• ${item}`);
          });
        }

        if (releaseNotes.length > 0) {
          return releaseNotes;
        }
      } else {
        console.warn(`[VersionDetector] Nenhum dado encontrado para a versão ${version}`);
      }
      
    } catch (error) {
      console.error('[VersionDetector] Erro ao carregar changelog:', error);
    }

    return [
      '📦 Atualização da aplicação disponível',
      '🔧 Melhorias gerais de desempenho',
      '⚡ Correções e otimizações',
      '🚀 Novos recursos implementados'
    ];
  }, []);

  /**
   * 🔄 EXECUTAR VERIFICAÇÃO COMPLETA
   */
  const performUpdateCheck = useCallback(async () => {
    if (circuitOpenRef.current) {
      return;
    }

    try {
      const current = await getCurrentVersion();
      const latest = await checkForUpdates();

      setState(prev => ({ 
        ...prev, 
        currentVersion: current,
        latestVersion: latest,
        updateAvailable: latest && latest.isVersionChange ? compareVersions(current, latest) : false,
        lastChecked: Date.now()
      }));

      // 🚫 MODO DESENVOLVIMENTO: Apenas se forçado OU mudança real de versão
      if (process.env.NODE_ENV === 'development') {
        const isForced = localStorage.getItem('force-version-check') === 'true';
        
        if (latest && latest.isVersionChange && compareVersions(current, latest) && 
            (isForced || process.env.NODE_ENV === 'development') && !stateRef.current.showUpdateModal) {
          if (isForced) localStorage.removeItem('force-version-check');
          
          const releaseNotes = await getChangelogForVersion(latest.version);
          const latestWithChangelog = { ...latest, releaseNotes };
          
          setState(prev => ({ 
            ...prev, 
            latestVersion: latestWithChangelog,
            showUpdateModal: true 
          }));
        }
        return;
      }

      // 🌐 MODO PRODUÇÃO: APENAS para mudanças reais de versão SEMVER
      if (latest && latest.isVersionChange && compareVersions(current, latest) && !stateRef.current.showUpdateModal) {
        const releaseNotes = await getChangelogForVersion(latest.version);
        const latestWithChangelog = { ...latest, releaseNotes };

        setState(prev => ({ 
          ...prev, 
          latestVersion: latestWithChangelog,
          updateAvailable: true
        }));

        notification.info({
          key: 'version-update',
          message: '🚀 Nova versão disponível!',
          description: `Versão ${latest.version} com novas funcionalidades e melhorias.`,
          duration: 0,
          placement: 'topRight',
          btn: (
            <Button 
              type="primary" 
              size="small" 
              onClick={() => setState(prev => ({ ...prev, showUpdateModal: true }))}
            >
              Ver Novidades
            </Button>
          )
        });
      } else if (latest && !latest.isVersionChange) {
      }

    } catch (error) {
      setErrorCount(prev => prev + 1);
    }
  }, [getCurrentVersion, checkForUpdates, compareVersions, getChangelogForVersion]); // 🔧 DEPENDÊNCIAS LIMPAS

  /**
   * 🔄 REALIZAR ATUALIZAÇÃO COM PRESERVAÇÃO DE AUTH
   */
  const performUpdate = useCallback(async () => {
    setState(prev => ({ ...prev, isUpdating: true, updateProgress: 0 }));

    try {
      const progressSteps = [
        { progress: 20, message: 'Limpando cache do navegador...' },
        { progress: 40, message: 'Preparando nova versão...' },
        { progress: 60, message: 'Aplicando atualizações...' },
        { progress: 80, message: 'Finalizando instalação...' },
        { progress: 100, message: 'Atualização concluída!' }
      ];

      for (const step of progressSteps) {
        setState(prev => ({ ...prev, updateProgress: step.progress }));
        await new Promise(resolve => setTimeout(resolve, 800));
      }

      // 🔐 PRESERVAR DADOS DE AUTENTICAÇÃO ANTES DA LIMPEZA
      const preserveAuthData = () => {
        try {
          // Salvar dados críticos de autenticação no sessionStorage temporariamente
          const authKeys = ['user', 'tokenLogin', 'funcoes', 'admin', 'cliente'];
          const tempAuthData: any = {};
          
          // Buscar dados em todos os possíveis padrões de localStorage
          Object.keys(localStorage).forEach(key => {
            // Verificar se é chave de auth de qualquer sessão/cliente
            authKeys.forEach(authKey => {
              if (key.includes(authKey) || key === authKey) {
                tempAuthData[key] = localStorage.getItem(key);
              }
            });
          });
          
          // Salvar no sessionStorage como backup
          if (Object.keys(tempAuthData).length > 0) {
            sessionStorage.setItem('tempAuthBackup', JSON.stringify(tempAuthData));
            
            // Também salvar URL atual para restaurar depois
            sessionStorage.setItem('tempCurrentUrl', window.location.href);
            
            console.log('🔐 [VersionDetector] Dados de auth preservados para reload:', Object.keys(tempAuthData));
          }
        } catch (error) {
          console.warn('⚠️ [VersionDetector] Erro ao preservar dados de auth:', error);
        }
      };

      // 🧹 LIMPEZA SELETIVA DE CACHE (PRESERVAR AUTH)
      preserveAuthData();

      if ('caches' in window) {
        const cacheNames = await caches.keys();
        const deletionPromises = cacheNames.map(name => caches.delete(name));
        await Promise.all(deletionPromises);
      }

      // ✅ LIMPEZA SELETIVA: Remover apenas cache/version/build, PRESERVAR AUTH
      const keysToRemove = Object.keys(localStorage).filter(key => 
        (key.includes('cache') || 
        key.includes('version') || 
         key.includes('build')) &&
        // 🔐 NÃO REMOVER dados de autenticação
        !key.includes('user') &&
        !key.includes('tokenLogin') &&
        !key.includes('funcoes') &&
        !key.includes('admin') &&
        !key.includes('cliente') &&
        !key.includes('heartbeat') &&
        !key.includes('ownership')
      );
      
      keysToRemove.forEach(key => {
        localStorage.removeItem(key);
      });

      // 🔄 RELOAD INTELIGENTE: Manter URL atual se usuário está logado
      const currentUrl = window.location.href;
      const hasAuthData = localStorage.getItem('tokenLogin') || 
                         Object.keys(localStorage).some(key => key.includes('tokenLogin'));
      
      if (hasAuthData && !currentUrl.includes('/login')) {
        // ✅ USUÁRIO LOGADO: Reload na mesma URL com cache busting
        const url = new URL(currentUrl);
        url.searchParams.set('v', Date.now().toString());
        url.searchParams.set('build', state.latestVersion && state.latestVersion.buildHash || 'new');
        
        // Marcar que é um reload do VersionDetector
        url.searchParams.set('version_update', '1');
        
        window.location.href = url.toString();
      } else {
        // ❌ SEM AUTH: Reload padrão
      const timestamp = Date.now();
        const cacheBustingUrl = `${window.location.origin}${window.location.pathname}?v=${timestamp}&build=${state.latestVersion && state.latestVersion.buildHash || 'new'}&version_update=1`;
      
      window.location.href = cacheBustingUrl;
      }

    } catch (error) {
      notification.error({
        message: '❌ Erro na Atualização',
        description: 'Não foi possível completar a atualização. Tente recarregar a página manualmente (Ctrl+F5).',
        duration: 8
      });
      setState(prev => ({ ...prev, isUpdating: false, updateProgress: 0 }));
    }
  }, [state.latestVersion]);

  /**
   * ⏰ CONFIGURAÇÃO PERIÓDICA SILENCIOSA
   */
  useEffect(() => {
    // 🔧 VERIFICAÇÃO INICIAL (10 segundos após carregar)
    const initialTimeout = setTimeout(() => {
      if (!circuitOpenRef.current) {
        performUpdateCheck();
      }
    }, 10000);

    // 🔧 INTERVALO PRINCIPAL (30 minutos em produção)
    const checkInterval = process.env.NODE_ENV === 'development' 
      ? 60 * 60 * 1000  // 1 hora em desenvolvimento
      : 30 * 60 * 1000; // 🔧 ALTERADO: 30 minutos em produção (era 15 min)

    const interval = setInterval(() => {
      // 🔧 USAR REFS para valores atuais
      if (!circuitOpenRef.current && 
          errorCountRef.current < MAX_ERRORS && 
          !stateRef.current.showUpdateModal) {
        performUpdateCheck();
      }
    }, checkInterval);

    // 📊 REGISTRAR NO PERFORMANCE MONITOR
    if ((window as any).globalAdvancedManager) {
      try {
        (window as any).globalAdvancedManager.registerTimer(interval, 'VersionDetector');
      } catch (error) {
        // Silent error
      }
    }

    // 🔧 HANDLER DE FOCUS - Verificar apenas se passou tempo suficiente
    const handleFocus = () => {
      // 🔧 USAR REFS para valores atuais
      if (circuitOpenRef.current || 
          errorCountRef.current >= MAX_ERRORS || 
          stateRef.current.showUpdateModal) return;
      
      const timeSinceLastCheck = Date.now() - stateRef.current.lastChecked;
      const minTimeThreshold = process.env.NODE_ENV === 'development'
        ? 30 * 60 * 1000  // 30 minutos em desenvolvimento
        : 20 * 60 * 1000; // 🔧 ALTERADO: 20 minutos em produção (era 10 min)
        
      if (timeSinceLastCheck > minTimeThreshold) {
        performUpdateCheck();
      }
    };

    const handleBeforeUnload = () => {
      clearInterval(interval);
      clearTimeout(initialTimeout);
    };

    window.addEventListener('focus', handleFocus, { passive: true });
    window.addEventListener('beforeunload', handleBeforeUnload, { passive: true });

    return () => {
      clearInterval(interval);
      clearTimeout(initialTimeout);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      
      if ((window as any).globalAdvancedManager) {
        try {
          (window as any).globalAdvancedManager.unregisterTimer(interval, 'VersionDetector');
        } catch (error) {
          // Silent error
        }
      }
    };
  }, []); // 🔧 DEPENDÊNCIAS VAZIAS - useEffect roda apenas uma vez!

  /**
   * 🎨 MODAL DE ATUALIZAÇÃO - CHANGELOG SEMPRE VISÍVEL
   */
  const renderUpdateModal = () => {
    const { latestVersion, isUpdating, updateProgress, updateAvailable, currentVersion } = state;
    
    // Verificar se é visualização da versão atual ou nova versão
    const isCurrentVersionView = latestVersion && currentVersion && 
                                latestVersion.version === currentVersion.version && 
                                !updateAvailable;

    return (
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {isCurrentVersionView ? (
              <>
                <InfoCircleOutlined style={{ color: '#1890ff' }} />
                <span>Changelog da Versão Atual</span>
              </>
            ) : (
              <>
                <RocketOutlined style={{ color: '#1890ff' }} />
                <span>Nova Versão Disponível!</span>
              </>
            )}
          </div>
        }
        open={state.showUpdateModal}
        onCancel={() => setState(prev => ({ ...prev, showUpdateModal: false }))}
        footer={null}
        width={520}
        maskClosable={!isUpdating}
        closable={!isUpdating}
      >
        <div style={{ padding: '16px 0' }}>
          {!isUpdating ? (
            <>
              <div style={{ marginBottom: '20px' }}>
                {isCurrentVersionView ? (
                  <p style={{ fontSize: '16px', marginBottom: '12px', color: '#444' }}>
                    📄 Visualizando as novidades da versão atual
                  </p>
                ) : (
                  <p style={{ fontSize: '16px', marginBottom: '12px', color: '#444' }}>
                    🎉 Uma nova versão da aplicação está disponível!
                  </p>
                )}
                
                {/* INFORMAÇÕES DE VERSÃO */}
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  backgroundColor: isCurrentVersionView ? '#f0f8ff' : '#f0f8ff', 
                  padding: '12px', 
                  borderRadius: '6px',
                  fontSize: '13px',
                  marginBottom: '16px'
                }}>
                  <span>
                    <strong>📦 Versão Atual:</strong> {state.currentVersion && state.currentVersion.version || 'Desconhecida'}
                  </span>
                  {!isCurrentVersionView && (
                    <span>
                      <strong>🚀 Nova Versão:</strong> {latestVersion && latestVersion.version || 'Nova'}
                    </span>
                  )}
                </div>
                
                {/* CHANGELOG SEMPRE VISÍVEL */}
                {latestVersion && latestVersion.releaseNotes && (
                  <div style={{ 
                    backgroundColor: '#f6f8fa', 
                    padding: '14px', 
                    borderRadius: '8px',
                    marginBottom: '16px',
                    border: '1px solid #e1e4e8'
                  }}>
                    <h4 style={{ 
                      margin: '0 0 10px 0', 
                      color: '#1890ff',
                      fontSize: '14px',
                      fontWeight: 600
                    }}>
                      📋 Novidades da Versão {latestVersion.version}:
                    </h4>
                    
                    <div style={{ margin: 0, paddingLeft: '4px' }}>
                      {latestVersion.releaseNotes.map((note, index) => {
                        // Cabeçalhos de categoria (com emojis e **)
                        if (note.includes('**') && (
                          note.startsWith('🚀') || 
                          note.startsWith('⚡') || 
                          note.startsWith('🔧') || 
                          note.startsWith('🗑️')
                        )) {
                          return (
                            <div key={index} style={{ 
                              fontWeight: 'bold', 
                              marginTop: index > 0 ? '14px' : '0',
                              marginBottom: '6px',
                              color: '#1890ff',
                              fontSize: '13px'
                            }}>
                              {note.replace(/\*\*/g, '')}
                            </div>
                          );
                        }
                        // Itens da lista (começam com •)
                        else if (note.startsWith('•')) {
                          return (
                            <div key={index} style={{ 
                              marginBottom: '3px',
                              paddingLeft: '18px',
                              fontSize: '12px',
                              lineHeight: '1.5',
                              color: '#555'
                            }}>
                              {note}
                            </div>
                          );
                        }
                        // Outras notas
                        else {
                          return (
                            <div key={index} style={{ 
                              marginBottom: '5px',
                              fontSize: '12px',
                              lineHeight: '1.5',
                              color: '#666'
                            }}>
                              {note}
                            </div>
                          );
                        }
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* BOTÕES DE AÇÃO */}
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                {isCurrentVersionView ? (
                  // Botão apenas para fechar quando é visualização da versão atual
                  <Button 
                    type="primary"
                    onClick={() => setState(prev => ({ ...prev, showUpdateModal: false }))}
                    icon={<CloseOutlined />}
                  >
                    Fechar
                  </Button>
                ) : (
                  // Botões de atualização quando há nova versão
                  <>
                    <Button 
                      onClick={() => setState(prev => ({ ...prev, showUpdateModal: false }))}
                      icon={<CloseOutlined />}
                    >
                      Atualizar Depois
                    </Button>
                    <Button 
                      type="primary"
                      onClick={performUpdate}
                      icon={<ReloadOutlined />}
                      size="middle"
                    >
                      Atualizar Agora
                    </Button>
                  </>
                )}
              </div>
            </>
          ) : (
            /* TELA DE PROGRESSO */
            <div style={{ textAlign: 'center', padding: '30px 0' }}>
              <Spin size="large" style={{ marginBottom: '20px' }} />
              <h3 style={{ marginBottom: '16px', color: '#1890ff' }}>
                Atualizando aplicação...
              </h3>
              <Progress 
                percent={updateProgress} 
                strokeColor={{
                  '0%': '#108ee9',
                  '100%': '#87d068',
                }}
                style={{ marginBottom: '16px' }}
                showInfo={false}
              />
              <p style={{ color: '#666', fontSize: '14px', margin: 0 }}>
                ⚠️ Por favor, não feche esta janela durante a atualização
              </p>
            </div>
          )}
        </div>
      </Modal>
    );
  };

  // 🧪 COMANDOS DE DEBUG GLOBAIS (DISPONÍVEIS EM WINDOW)
  useEffect(() => {
    // Tornar funções disponíveis globalmente para debug
      (window as any).versionDetector = {
      // Comando principal de ajuda
      help: () => {
        console.log(`
🔄 VERSION DETECTOR - Comandos Disponíveis:

📊 INFORMAÇÕES:
• getCurrentInfo()     - Ver versão atual e build hash
• getStats()          - Estatísticas de verificações e erros
• listVersions()      - Listar versões encontradas no changelog

🔧 VERIFICAÇÕES:
• forceCheck()        - Forçar verificação de nova versão
• checkChangelog()    - Verificar apenas changelog local
• debugFetch()        - Testar conectividade com endpoints

🎭 SIMULAÇÕES:
• simulateUpdate('2.0.0')    - Simular nova versão específica
• simulateHotfix()           - Simular hotfix (mesma versão, build diferente)
• testVersionBehavior()      - Testar comportamento completo

🔄 ATUALIZAÇÕES:
• performSafeUpdate()        - Atualização com preservação de auth ⭐ NOVO!
• performUnsafeUpdate()      - Atualização tradicional (pode perder auth)

🎨 INTERFACE:
• showModal()         - Exibir modal de atualização
• hideModal()         - Fechar modal
• showIndicator()     - Mostrar indicador de versão

🧹 LIMPEZA:
• clearCache()        - Limpar cache do navegador
• clearAllData()      - Reset completo (localStorage + cache)

🔧 ESTADOS:
• resetErrors()       - Resetar contador de erros
• openCircuit()       - Abrir circuit breaker
• closeCircuit()      - Fechar circuit breaker

🔐 AUTH DEBUG: ⭐ NOVO!
• testAuthPreservation()     - Testar preservação de dados de auth
• simulateAuthLoss()         - Simular perda de dados de auth
• checkAuthData()            - Verificar dados de auth atuais
• restoreAuthFromBackup()    - Restaurar auth do sessionStorage

Digite versionDetector.COMANDO() para usar qualquer função.
        `);
      },

      // ✅ NOVOS COMANDOS DE AUTH DEBUG
      testAuthPreservation: () => {
        console.log('🔐 [DEBUG] Testando preservação de dados de auth...');
        
        const authKeys = ['user', 'tokenLogin', 'funcoes', 'admin', 'cliente'];
        const foundAuthData: any = {};
        
        Object.keys(localStorage).forEach(key => {
          authKeys.forEach(authKey => {
            if (key.includes(authKey) || key === authKey) {
              foundAuthData[key] = localStorage.getItem(key);
            }
          });
        });
        
        console.log('📊 Dados de auth encontrados:', Object.keys(foundAuthData));
        console.log('📝 Detalhes:', foundAuthData);
        
        if (Object.keys(foundAuthData).length > 0) {
          console.log('✅ Sistema tem dados de auth para preservar');
        } else {
          console.log('❌ Nenhum dado de auth encontrado');
        }
        
        return foundAuthData;
      },

      simulateAuthLoss: () => {
        console.log('🚨 [DEBUG] Simulando perda de dados de auth (removendo do localStorage)...');
        
        const authKeys = ['user', 'tokenLogin', 'funcoes', 'admin', 'cliente'];
        const removedKeys: string[] = [];
        
        Object.keys(localStorage).forEach(key => {
          authKeys.forEach(authKey => {
            if (key.includes(authKey) || key === authKey) {
              localStorage.removeItem(key);
              removedKeys.push(key);
            }
          });
        });
        
        console.log('🗑️ Chaves removidas:', removedKeys);
        console.log('⚠️ Agora teste versionDetector.performSafeUpdate() para ver se preserva');
      },

      checkAuthData: () => {
        console.log('🔍 [DEBUG] Verificando dados de auth atuais...');
        
        const localStorage_auth = (window as any).versionDetector.testAuthPreservation();
        
        const sessionStorage_backup = sessionStorage.getItem('tempAuthBackup');
        console.log('📦 Backup no sessionStorage:', sessionStorage_backup ? 'SIM' : 'NÃO');
        
        if (sessionStorage_backup) {
          try {
            const backup = JSON.parse(sessionStorage_backup);
            console.log('📝 Conteúdo do backup:', Object.keys(backup));
          } catch (e) {
            console.log('❌ Backup corrompido');
          }
        }
        
        return {
          localStorage: localStorage_auth,
          sessionBackup: sessionStorage_backup
        };
      },

      restoreAuthFromBackup: () => {
        console.log('🔄 [DEBUG] Tentando restaurar auth do sessionStorage...');
        
        try {
          const tempAuthBackup = sessionStorage.getItem('tempAuthBackup');
          
          if (!tempAuthBackup) {
            console.log('❌ Nenhum backup encontrado no sessionStorage');
            return false;
          }
          
          const authData = JSON.parse(tempAuthBackup);
          console.log('📦 Restaurando dados:', Object.keys(authData));
          
          Object.keys(authData).forEach(key => {
            if (authData[key]) {
              localStorage.setItem(key, authData[key]);
            }
          });
          
          console.log('✅ Dados restaurados com sucesso');
          return true;
          } catch (error) {
          console.log('❌ Erro ao restaurar:', error);
          return false;
        }
      },

      performSafeUpdate: () => {
        console.log('🔐 [DEBUG] Executando atualização SEGURA (com preservação de auth)...');
        performUpdate();
      },

      performUnsafeUpdate: () => {
        console.log('⚠️ [DEBUG] Executando atualização TRADICIONAL (pode perder auth)...');
        
        // Simular atualização sem preservação de auth
        window.location.href = `${window.location.origin}${window.location.pathname}?v=${Date.now()}&build=debug&unsafe=1`;
      },

      // Comandos existentes mantidos
      getCurrentInfo: () => {
        const current = getCurrentVersion();
        current.then(info => {
          console.log('📊 Versão Atual:', info);
          console.log('🔗 Build Hash:', info.buildHash);
          console.log('🕐 Timestamp:', new Date(info.timestamp).toLocaleString());
        });
      },

      forceCheck: () => {
        console.log('🔄 Forçando verificação de atualização...');
        performUpdateCheck();
      },

      showModal: () => {
        console.log('🎨 Exibindo modal de atualização...');
        setState(prev => ({ ...prev, showUpdateModal: true }));
        },
        
        hideModal: () => {
        console.log('❌ Fechando modal de atualização...');
          setState(prev => ({ ...prev, showUpdateModal: false }));
        },

      simulateUpdate: (version: string) => {
        console.log(`🎭 Simulando nova versão: ${version}`);
        setState(prev => ({
          ...prev,
          latestVersion: {
            version,
            buildHash: 'simulated_' + Date.now(),
            timestamp: Date.now(),
            releaseNotes: [`Versão ${version} simulada para teste`]
          },
          updateAvailable: true,
          showUpdateModal: true
        }));
      },

      debugFetch: async () => {
        console.log('🌐 Testando conectividade...');
        try {
            const safeFetch = window.fetch.bind(window);
            
          const testUrls = [
            '/api/version',
            '/package.json', 
            '/changelog.json'
          ];
          
          for (const url of testUrls) {
            try {
              const response = await safeFetch(url, { cache: 'no-cache' });
              console.log(`✅ ${url}: ${response.status} ${response.statusText}`);
            } catch (error: any) {
              console.log(`❌ ${url}: ${error.message}`);
            }
          }
          } catch (error) {
          console.log('❌ Erro geral:', error);
        }
      },

      clearCache: async () => {
        console.log('🧹 Limpando cache...');
        if ('caches' in window) {
          const cacheNames = await caches.keys();
          await Promise.all(cacheNames.map(name => caches.delete(name)));
          console.log('✅ Cache limpo');
        }
      }
    };

    // Log inicial apenas em desenvolvimento
    if (process.env.NODE_ENV === 'development') {
      console.log('🔄 VersionDetector carregado. Digite versionDetector.help() para ver comandos disponíveis.');
    }

    return () => {
      // Cleanup
      delete (window as any).versionDetector;
    };
  }, [performUpdate, performUpdateCheck, getCurrentVersion]);

  return (
    <>
      {renderUpdateModal()}
    </>
  );
};

// 🎯 COMPONENTE SIMPLES PARA INDICADORES DO LAYOUT
export const SimpleVersionIndicator: React.FC<{ 
  style?: React.CSSProperties;
  onClick?: () => void;
}> = ({ style, onClick }) => {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [latestVersion, setLatestVersion] = useState<string>('');
  const [hasChecked, setHasChecked] = useState(false);

  useEffect(() => {
    // 🔧 VERIFICAR APENAS UMA VEZ - evitar múltiplas chamadas
    if (hasChecked) return;

    // Verificar se há versão disponível
    const checkUpdate = async () => {
      try {
        setHasChecked(true); // Marcar como verificado antes mesmo da chamada
        
        const packageJson = require('../../../package.json');
        
        // 🔧 TENTAR CACHE PRIMEIRO - evitar fetch desnecessário
        const cacheKey = 'simple_version_check_cache';
        const cached = localStorage.getItem(cacheKey);
        
        if (cached) {
          try {
            const { latest, timestamp } = JSON.parse(cached);
            const now = Date.now();
            
            // 🔧 CACHE POR 30 MINUTOS - evitar requests excessivos
            if (now - timestamp < 30 * 60 * 1000) {
              if (latest && latest !== packageJson.version) {
                setUpdateAvailable(true);
                setLatestVersion(latest);
              }
              return; // Usar cache válido
            }
          } catch (error) {
            // Cache corrompido, continuar com fetch
            localStorage.removeItem(cacheKey);
          }
        }
        
        let changelog;
        
        try {
          changelog = await import('../../app/changelog.json');
          changelog = changelog.default || changelog;
        } catch (importError) {
          // 🔧 FALLBACK COM FETCH - apenas se import falhar
          try {
            const safeFetch = window.fetch.bind(window);
            const response = await safeFetch('/changelog.json', { cache: 'no-cache' });
            if (response.ok) {
              changelog = await response.json();
            } else {
              return;
            }
          } catch (fetchError) {
            // Falha total, não mostrar indicador
            return;
          }
        }

        const versions = Object.keys(changelog).filter(key => 
          /^\d+\.\d+\.\d+$/.test(key)
        ).sort((a, b) => {
          const parseVersion = (version: string) => {
            const parts = version.split('.').map(n => parseInt(n, 10) || 0);
            return { major: parts[0] || 0, minor: parts[1] || 0, patch: parts[2] || 0 };
          };

          const versionA = parseVersion(a);
          const versionB = parseVersion(b);

          if (versionB.major !== versionA.major) return versionB.major - versionA.major;
          if (versionB.minor !== versionA.minor) return versionB.minor - versionA.minor;
          return versionB.patch - versionA.patch;
        });

        const latest = versions[0];
        
        // 🔧 SALVAR NO CACHE - evitar futuras chamadas
        const cacheData = {
          latest: latest || '',
          timestamp: Date.now()
        };
        localStorage.setItem(cacheKey, JSON.stringify(cacheData));
        
        if (latest && latest !== packageJson.version) {
          setUpdateAvailable(true);
          setLatestVersion(latest);
        }
      } catch (error) {
        // Silent error - não afetar o usuário
        setHasChecked(true);
      }
    };

    // 🔧 DELAY INICIAL - evitar chamada imediata
    const timer = setTimeout(checkUpdate, 2000);
    return () => clearTimeout(timer);
  }, [hasChecked]); // 🔧 DEPENDÊNCIA ÚNICA - controla re-execução

  if (!updateAvailable) {
    return null;
  }

  const handleClick = () => {
    if (onClick) {
      onClick();
    } else {
      // Forçar o VersionDetector principal a mostrar o modal
      if ((window as any).versionDetector) {
        if (updateAvailable && (window as any).versionDetector.showModal) {
          (window as any).versionDetector.showModal();
        } else if ((window as any).versionDetector.showCurrentChangelog) {
          // Se não há atualização, mostrar changelog da versão atual
          (window as any).versionDetector.showCurrentChangelog();
        }
      }
    }
  };

  return (
    <div 
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        backgroundColor: '#52c41a',
        color: 'white',
        padding: '4px 8px',
        borderRadius: '12px',
        fontSize: '11px',
        fontWeight: 500,
        cursor: 'pointer',
        boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
        transition: 'all 0.3s',
        ...style
      }} 
      onClick={handleClick}
      title={`Nova versão ${latestVersion} disponível! Clique para atualizar`}
    >
      <RocketOutlined style={{ fontSize: '10px' }} />
      <span>v{latestVersion}</span>
    </div>
  );
};

export default VersionDetector; 